import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import {
  createAccountingEntriesBatchFromTemplate,
  getCostCenters,
} from '../../../../../../../../lib/api/accounting';
import { CostCenter } from '../../../../types';
import CreateBusinessDialog from './components/CreateBusinessDialog';
import EntriesTabs from './components/EntriesTabs';
import InvalidEntriesTable from './components/InvalidEntriesTable';
import LinesModal from './components/LinesModal';
import SummaryAlerts from './components/SummaryAlerts';
import UploadSection from './components/UploadSection';
import ValidEntriesTable from './components/ValidEntriesTable';
import { ERROR_MESSAGES, EXCEL_TEMPLATE, SUCCESS_MESSAGES } from './const';
import { EntriesImporterProps, ProcessedEntry } from './types';
import {
  calculateTotalLines,
  extractErrorMessage,
  parseExcelFile,
  prepareEntriesForImport,
  processEntries,
} from './utils';

const EntriesImporter: React.FC<EntriesImporterProps> = ({
  open,
  setOpen,
  nodes,
  entryClasses,
  accountingPeriods,
  companyId,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [validEntries, setValidEntries] = useState<ProcessedEntry[]>([]);
  const [invalidEntries, setInvalidEntries] = useState<ProcessedEntry[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [linesModalOpen, setLinesModalOpen] = useState(false);
  const [selectedEntryForView, setSelectedEntryForView] =
    useState<ProcessedEntry | null>(null);
  const [missingRuts, setMissingRuts] = useState<string[]>([]);
  const [openCreateBusinessDialog, setOpenCreateBusinessDialog] =
    useState(false);

  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const response = await getCostCenters(dispatch);
        setCostCenters(response || []);
      } catch (error) {
        console.error('Error fetching cost centers:', error);
        setCostCenters([]);
      }
    };
    if (open) {
      void fetchCostCenters();
    }
  }, [open, dispatch]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    setUploadedFileName(uploadedFile.name);

    if (
      !uploadedFile.name.endsWith('.xlsx') &&
      !uploadedFile.name.endsWith('.xls')
    ) {
      showAlert(dispatch, 'error', ERROR_MESSAGES.FILE_FORMAT_NOT_SUPPORTED);
      return;
    }

    setLoading(true);
    try {
      const parsedData = await parseExcelFile(uploadedFile);
      const processedData = processEntries(
        parsedData,
        { nodes, costCenters, entryClasses },
        { nodes, accountingPeriods },
        companyId,
      );

      setValidEntries(processedData.valid);
      setInvalidEntries(processedData.invalid);
      setSelectedEntries(
        processedData.valid.map((_, index) => `entry-${index}`),
      );
      // Establecer tab inicial: si hay válidos, mostrar válidos; si no, mostrar errores
      setActiveTab(
        processedData.valid.length > 0
          ? 0
          : processedData.invalid.length > 0
            ? 1
            : 0,
      );

      if (
        processedData.valid.length === 0 &&
        processedData.invalid.length > 0
      ) {
        showAlert(dispatch, 'warning', ERROR_MESSAGES.NO_VALID_ENTRIES);
      } else if (processedData.valid.length > 0) {
        showAlert(
          dispatch,
          'success',
          SUCCESS_MESSAGES.ENTRIES_FOUND(processedData.valid.length),
        );
      }
    } catch (error) {
      console.error('Error processing file:', error);
      showAlert(dispatch, 'error', ERROR_MESSAGES.FILE_PROCESSING_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedEntries.length === 0) {
      showAlert(dispatch, 'warning', ERROR_MESSAGES.NO_ENTRIES_SELECTED);
      return;
    }

    const entriesToImport = prepareEntriesForImport(
      selectedEntries,
      validEntries,
    );

    setImporting(true);
    try {
      await createAccountingEntriesBatchFromTemplate(dispatch, entriesToImport);
      showAlert(
        dispatch,
        'success',
        SUCCESS_MESSAGES.ENTRIES_IMPORTED(entriesToImport.length),
      );
      onSuccess();
      handleClose();
    } catch (error: unknown) {
      console.error('Error importing entries:', error);
      const errorResult = extractErrorMessage(error);
      if (errorResult) {
        showAlert(dispatch, 'error', errorResult.message);
        if (errorResult.missingRuts && errorResult.missingRuts.length > 0) {
          setMissingRuts(errorResult.missingRuts);
        }
      } else {
        showAlert(dispatch, 'error', ERROR_MESSAGES.IMPORT_ERROR);
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setValidEntries([]);
    setInvalidEntries([]);
    setSelectedEntries([]);
    setUploadedFileName(null);
    setActiveTab(0);
    setLinesModalOpen(false);
    setSelectedEntryForView(null);
    setMissingRuts([]);
    setOpenCreateBusinessDialog(false);
  };

  const handleViewLines = (
    e: React.MouseEvent,
    processedEntry: ProcessedEntry,
  ) => {
    e.stopPropagation();
    setSelectedEntryForView(processedEntry);
    setLinesModalOpen(true);
  };

  const handleCloseLinesModal = () => {
    setLinesModalOpen(false);
    setSelectedEntryForView(null);
  };

  const handleSelectEntry = (entryId: string) => {
    setSelectedEntries((prev) => {
      if (prev.includes(entryId)) {
        return prev.filter((id) => id !== entryId);
      }
      return [...prev, entryId];
    });
  };

  const handleSelectAllEntries = () => {
    if (selectedEntries.length === validEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(validEntries.map((_, index) => `entry-${index}`));
    }
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(EXCEL_TEMPLATE);

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'template_importacion_asientos.xlsx');
  };

  const totalLines = useMemo(
    () => calculateTotalLines(validEntries),
    [validEntries],
  );

  const totalValidEntries = validEntries.length;
  const totalInvalidEntries = invalidEntries.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Importar Asientos contables desde Excel
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading || importing ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <Grid container spacing={3}>
            <UploadSection
              onFileChange={handleFileChange}
              uploadedFileName={uploadedFileName}
              onDownloadTemplate={downloadTemplate}
            />

            <SummaryAlerts
              totalValidEntries={totalValidEntries}
              totalInvalidEntries={totalInvalidEntries}
              totalLines={totalLines}
            />

            <EntriesTabs
              activeTab={activeTab}
              onTabChange={(_, newValue) => setActiveTab(newValue)}
              totalValidEntries={totalValidEntries}
              totalInvalidEntries={totalInvalidEntries}
            />

            {totalValidEntries > 0 && activeTab === 0 && (
              <ValidEntriesTable
                entries={validEntries}
                selectedEntries={selectedEntries}
                onSelectEntry={handleSelectEntry}
                onSelectAll={handleSelectAllEntries}
                onViewLines={handleViewLines}
              />
            )}

            {totalInvalidEntries > 0 && activeTab === 1 && (
              <InvalidEntriesTable
                entries={invalidEntries}
                onViewLines={handleViewLines}
              />
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={handleClose}
          color="secondary"
          disabled={importing}
        >
          Cancelar
        </CustomButton>
        {missingRuts.length > 0 && (
          <CustomButton
            onClick={() => setOpenCreateBusinessDialog(true)}
            color="primary"
            variant="outlined"
            disabled={importing}
          >
            Crear Empresas ({missingRuts.length})
          </CustomButton>
        )}
        <CustomButton
          onClick={handleConfirmSelection}
          color="primary"
          disabled={selectedEntries.length === 0 || importing}
        >
          {importing
            ? 'Importando...'
            : `Importar Selección (${selectedEntries.length})`}
        </CustomButton>
      </DialogActions>

      <LinesModal
        open={linesModalOpen}
        onClose={handleCloseLinesModal}
        entry={selectedEntryForView}
        nodes={nodes}
        costCenters={costCenters}
      />

      <CreateBusinessDialog
        open={openCreateBusinessDialog}
        onClose={() => {
          setOpenCreateBusinessDialog(false);
          setMissingRuts([]);
        }}
        dispatch={dispatch}
        missingRuts={missingRuts}
        onBusinessCreated={() => {
          setMissingRuts([]);
        }}
      />
    </Dialog>
  );
};

export default EntriesImporter;
